import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMatches } from '../hooks/useMatches';
import { useAuth } from '../context/AuthContext';
import { useDailyAiCards } from '../hooks/useDailyAiCards';
import api from '../services/api';
import MatchCard from '../components/MatchCard';
import { getConfidenceClass, getEdgeClass } from '../utils/cardMetrics';

const Dashboard = () => {
  const { isPremium, isAdmin } = useAuth();
  const { liveMatches, todayMatches, loading, error, liveNotice, liveWarning } = useMatches();
  const { cards, lastUpdated, source } = useDailyAiCards();

  const [valueSummary, setValueSummary] = useState({
    realCount: 0,
    nearCount: 0,
    pricedCount: 0,
    strongestEdge: null
  });

  useEffect(() => {
    let active = true;

    const loadValueSummary = async () => {
      try {
        const data = await api.getValueBetSummary();
        if (!active) return;

        const realValueBets = Array.isArray(data?.realValueBets) ? data.realValueBets : [];
        const strongestReal = realValueBets.reduce((best, item) => {
          if (!best || Number(item?.edge || 0) > Number(best?.edge || 0)) return item;
          return best;
        }, null);

        setValueSummary({
          realCount: Number(data?.summary?.realCount || 0),
          nearCount: Number(data?.summary?.nearCount || 0),
          pricedCount: Number(data?.summary?.pricedCount || 0),
          strongestEdge: strongestReal?.edge ?? null
        });
      } catch (loadError) {
        if (!active) return;
        setValueSummary({
          realCount: 0,
          nearCount: 0,
          pricedCount: 0,
          strongestEdge: null
        });
      }
    };

    loadValueSummary();

    return () => {
      active = false;
    };
  }, []);

  const strongestAnalyses = useMemo(() => cards.slice(0, 5), [cards]);

  const updateText =
    source === 'fallback'
      ? 'Son başarılı yayın vitrinde tutuluyor.'
      : source === 'empty'
        ? 'Otomatik AI yayını bekleniyor.'
        : 'Günlük premium vitrin hazır.';

  return (
    <div className="page dashboard premium-dashboard dashboard-v2">
      <section className="dashboard-hero-v2">
        <div className="dashboard-hero-content">
          <span className="hero-badge">Premium Veri Terminali</span>

          <h1>
            Tahmin değil,
            <span> veriyle hareket et.</span>
          </h1>

          <p>
            Yapay zeka destekli maç analizleri, value bet sinyalleri ve premium kupon akışı tek panelde.
          </p>

          <div className="dashboard-hero-actions">
            <Link to="/analysis" className="btn-primary">AI Analizine Git</Link>
            <Link to="/premium-coupons" className="ghost-btn">Premium Kuponlar</Link>
          </div>
        </div>

        <div className="dashboard-status-card">
          <div>
            <span>Son Güncelleme</span>
            <strong>{lastUpdated ? new Date(lastUpdated).toLocaleString('tr-TR') : 'Bekleniyor'}</strong>
          </div>
          <p>{updateText}</p>

          <div className="dashboard-status-grid">
            <div>
              <small>Gerçek Value</small>
              <b>{valueSummary.realCount}</b>
            </div>
            <div>
              <small>Yakın Value</small>
              <b>{valueSummary.nearCount}</b>
            </div>
            <div>
              <small>En Güçlü Edge</small>
              <b>
                {valueSummary.strongestEdge !== null
                  ? `${valueSummary.strongestEdge > 0 ? '+' : ''}${Number(valueSummary.strongestEdge).toFixed(1)}`
                  : '-'}
              </b>
            </div>
          </div>
        </div>
      </section>

      {error ? (
        <section className="content-panel">
          <div className="premium-empty-card">{error}</div>
        </section>
      ) : null}

      <section className="content-panel dashboard-section-v2">
        <div className="section-heading">
          <div>
            <h2>Günün En Güçlü Seçimleri</h2>
            <p>Model gücü, edge ve veri kalitesi en yüksek seçimler.</p>
          </div>
          <Link to="/analysis" className="section-link">Tümünü Gör</Link>
        </div>

        {strongestAnalyses.length === 0 ? (
          <div className="premium-empty-card">
            {source === 'empty' ? 'Otomatik AI yayını bekleniyor.' : 'Henüz analiz yok.'}
          </div>
        ) : (
          <div className="strongest-grid premium-terminal-grid">
            {strongestAnalyses.map((analysis) => (
              <article
                key={analysis.id || analysis._id || `${analysis.matchId}-${analysis.market}`}
                className="strongest-card premium-terminal-card dashboard-pick-card"
              >
                <div className="strongest-card-top">
                  <span className={`terminal-category ${String(analysis.category || '').toUpperCase()}`}>
                    {analysis.level || analysis.category || 'Model Seçimi'}
                  </span>
                  <span className="terminal-league">{analysis.league?.name || '-'}</span>
                </div>

                <strong>{analysis.homeTeam?.name} - {analysis.awayTeam?.name}</strong>
                <p>{analysis.market}</p>

                <div className="strongest-meta">
                  <span>Model %{analysis.modelProbability || 0}</span>
                  <span className={`metric-tone ${getConfidenceClass(analysis.confidence || 0)}`}>
                    Güven %{analysis.confidence || 0}
                  </span>
                  {analysis.edge !== null && analysis.edge !== undefined ? (
                    <span className={`edge-tone ${getEdgeClass(analysis.edge)}`}>
                      Edge {analysis.edge > 0 ? '+' : ''}{analysis.edge}
                    </span>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {liveMatches.length > 0 ? (
        <section className="content-panel dashboard-section-v2">
          <div className="section-heading">
            <div>
              <h2>Canlı Maçlar</h2>
              <p>Güncel veri akışında izlenen canlı karşılaşmalar.</p>
            </div>
            <Link to="/live" className="section-link">Canlı Maçlar</Link>
          </div>

          <div className="live-home-grid">
            {liveMatches.slice(0, 4).map((match) => (
              <MatchCard key={match.id || match._id} match={match} isLive={true} liveLayout="home-app" />
            ))}
          </div>
        </section>
      ) : (liveNotice || liveWarning) ? (
        <section className="content-panel dashboard-section-v2">
          <div className="section-heading">
            <div>
              <h2>Canlı Maçlar</h2>
              <p>Canlı veri akışı geçici olarak yavaşladı.</p>
            </div>
            <Link to="/live" className="section-link">Canlı Maçlar</Link>
          </div>
          <div className="premium-empty-card">{liveWarning || liveNotice}</div>
        </section>
      ) : null}
      <section className="content-panel dashboard-premium-strip">
        <div>
          <span className="hero-badge">Premium Fırsatlar</span>
          <h2>Bugün Value Bet Var mı?</h2>
          <p>Ana sayfada genel durum gösterilir. Detaylı market ve oran analizi premium alandadır.</p>
        </div>

        {(isPremium || isAdmin) ? (
          <Link to="/premium-opportunities" className="btn-primary">Premium'da Gör</Link>
        ) : (
          <Link to="/premium-preview?feature=opportunities" className="btn-primary">Premium'da Gör</Link>
        )}
      </section>

      <section className="content-panel dashboard-section-v2">
        <div className="section-heading">
          <div>
            <h2>AI Analiz Vitrini</h2>
            <p>Hazır yayınlanan premium AI kartları buradan okunur.</p>
          </div>
          <Link to="/analysis" className="section-link">AI Analiz</Link>
        </div>

        <div className="matches-grid compact-grid">
          {loading ? (
            <div className="premium-empty-card">Maç vitrini yükleniyor.</div>
          ) : todayMatches.length === 0 ? (
            <div className="premium-empty-card">Bugün için vitrin maçı bulunmuyor.</div>
          ) : (
            todayMatches.slice(0, 4).map((match) => (
              <MatchCard key={match.id || match._id} match={match} />
            ))
          )}
        </div>
      </section>

      <section className="content-panel dashboard-section-v2">
        <div className="section-heading">
          <div>
            <h2>Premium Kısa Yollar</h2>
            <p>Premium, riskli ve kupon akışına hızlı erişim.</p>
          </div>
        </div>

        <div className="premium-shortcuts-grid">
          <Link to="/premium-opportunities" className="premium-shortcut-card">
            <span className="hero-badge">Value Bet</span>
            <strong>Value Bet Durumu</strong>
            <p>Günlük değerli market sinyallerini premium alanda gör.</p>
          </Link>

          <Link to="/risky-analysis" className="premium-shortcut-card">
            <span className="hero-badge">Riskli</span>
            <strong>Riskli Denenebilir</strong>
            <p>Agresif marketleri model, edge ve risk seviyesi ile incele.</p>
          </Link>

          <Link to="/premium-coupons" className="premium-shortcut-card">
            <span className="hero-badge">Kupon</span>
            <strong>Premium Kuponlar</strong>
            <p>Gündüz ve akşam premium kupon paketlerini aç.</p>
          </Link>
        </div>
      </section>


    </div>
  );
};

export default Dashboard;